import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Observable, map, catchError, throwError } from 'rxjs';
import { environment } from '../services/global-environment';

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // Upload single file (JWT: companyid is retrieved from backend)
  uploadFile(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file); // 後端期望的欄位名稱
    formData.append('filename', file.name);

    return this.http.post(`${this.apiUrl}/api/storage/upload`, formData, {
      reportProgress: true,
      observe: 'events',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    }).pipe(
      map(event => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          return {
            type: 'progress',
            progress: Math.round(100 * event.loaded / event.total)
          };
        } else if (event.type === HttpEventType.Response) {
          return {
            type: 'success',
            response: event.body
          };
        }
        return event;
      }),
      catchError(error => {
        console.error('檔案上傳失敗:', error);
        return throwError(() => error);
      })
    );
  }

  // Upload multiple files (JWT: companyid is retrieved from backend)
  uploadMultipleFiles(files: File[]): Observable<any> {
    if (files.length === 0) {
      return throwError(() => new Error('沒有檔案可以上傳'));
    }

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file); // 多檔案上傳端點期望的欄位名稱
    });

    return this.http.post(`${this.apiUrl}/api/storage/upload-multiple`, formData, {
      reportProgress: true,
      observe: 'events',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    }).pipe(
      map(event => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          return {
            type: 'progress',
            progress: Math.round(100 * event.loaded / event.total)
          };
        } else if (event.type === HttpEventType.Response) {
          return {
            type: 'success',
            response: event.body
          };
        }
        return event;
      }),
      catchError(error => {
        console.error('批次檔案上傳失敗:', error);
        return throwError(() => error);
      })
    );
  }

  // Generate file view URL (JWT: companyid is retrieved from backend)
  generateViewUrl(blobName: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/storage/view-url`, {
      blobName
    }).pipe(
      catchError(error => {
        console.error('產生瀏覽連結失敗:', error);
        return throwError(() => error);
      })
    );
  }

  // Generate file download URL (JWT: companyid is retrieved from backend)
  generateDownloadUrl(blobName: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/storage/download-url`, {
      blobName
    }).pipe(
      catchError(error => {
        console.error('產生下載連結失敗:', error);
        return throwError(() => error);
      })
    );
  }

  // Delete file (JWT: companyid is retrieved from backend)
  deleteFile(blobName: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/storage/delete`, {
      blobName
    }).pipe(
      catchError(error => {
        console.error('刪除檔案失敗:', error);
        return throwError(() => error);
      })
    );
  }

  // Batch generate view/download URLs (JWT: companyid is retrieved from backend)
  batchGenerateUrls(blobNames: string[]): Observable<any> {
    const requests = blobNames.map(blobName => ({
      blobName
    }));

    return this.http.post(`${this.apiUrl}/api/storage/batch-urls`, {
      requests
    }).pipe(
      catchError(error => {
        console.error('批次產生連結失敗:', error);
        return throwError(() => error);
      })
    );
  }

  // Generate long-term view URL (JWT: companyid is retrieved from backend)
  generateLongTermViewUrl(blobName: string, expiryDays: number = 365): Observable<any> {
    const expiryMinutes = expiryDays * 24 * 60; // 轉換天數為分鐘
    return this.http.post(`${this.apiUrl}/api/storage/view-url`, {
      blobName,
      expiryMinutes // 傳遞自訂的過期時間
    }).pipe(
      catchError(error => {
        console.error('產生長效瀏覽連結失敗:', error);
        return throwError(() => error);
      })
    );
  }
}
